/**
 * The live appeal on the donor's own phone (GH-937).
 *
 * While the room is being called from the stage, this card puts the ask in the donor's hand with
 * two ways to answer it (owner, 2026-10-10): **Pledge is primary** — it raises the donor's hand,
 * counts on the rung and in the committed figure, and moves no money now — and **Give**, in the
 * product's green, pays the same amount through the ordinary rails. Both go through the existing
 * checkout with the amount fixed to the rung's: the server refuses any other number, so the card
 * never offers one.
 *
 * Three rules inherited from the payload's own contract (`appealPublicSchema`):
 *
 *  - **No donor.** The card draws counts and amounts and nothing else — it never shows who else
 *    answered, and never the hands/pledges/gifts split, which is the console's alone.
 *  - **Committed is not received.** The total here is labelled "committed", in the warning tone
 *    the product uses for promises, and is never added to the thermometer above it.
 *  - **The data is the goal poll's.** This card renders what `EventPage`'s existing four-second
 *    read already carries; it loads nothing itself (the GH-919 lesson: the screen drives the
 *    poll, never a card).
 *
 * The ask reads as the organizer wrote it, through the same `appealAskWords`/`appealCountWords`
 * the console and the wall use — "Feed 10 orphans @ $50.00 each", or the plain amount — so the
 * phone, the projector and the console say one thing.
 */
import {
  appealAskWords,
  appealCountWords,
  formatMoney,
  type AppealPublic,
} from "@givehere/contracts";
import type { AppealResponse } from "./appeal-response.js";

export function AppealCard({
  appeal,
  answered,
  confirmation = null,
  notice = null,
  busy,
  onRespond,
}: {
  appeal: AppealPublic;
  /** This tab's earlier answer, when it made one — the card acknowledges instead of asking again. */
  answered: AppealResponse | null;
  /**
   * The server's own sentence after a one-tap pledge from this card (owner 2026-10-10), shown
   * verbatim: "nothing has been charged" belongs to the system of record, not to this bundle.
   */
  confirmation?: string | null;
  /** The server's words when a one-tap answer was refused — the promise (warning) tone. */
  notice?: string | null;
  busy: boolean;
  onRespond: (intent: "pledge" | "give") => void;
}) {
  const rung = appeal.currentRung;
  const amount = formatMoney(rung.amount);

  return (
    <section className="appealcard" aria-labelledby="appealcard-ask">
      <p className="appealcard__kind">Appeal — now calling</p>
      {/* `dir="auto"` because the words are the organizer's and may read right-to-left. */}
      <p className="appealcard__ask" id="appealcard-ask" dir="auto">
        {appealAskWords(rung)}
      </p>
      <p className="appealcard__count" dir="auto">
        {appealCountWords(rung.committedCount, rung)}
      </p>

      {notice !== null && answered === null && (
        <p className="appealcard__notice" role="status" dir="auto">
          {notice}
        </p>
      )}

      {answered !== null ? (
        /*
          Answered already, in this tab: say so and stop asking. The figures above keep moving
          with the poll, so the donor watches their own hand join the count. A one-tap pledge
          shows the server's own sentence instead of this bundle's.
        */
        <p className="appealcard__answered" role="status">
          {confirmation !== null
            ? confirmation
            : answered.kind === "pledged"
              ? `Your hand is up at ${formatMoney(answered.amount)}. Nothing is charged now.`
              : `Thank you — you gave ${formatMoney(answered.amount)}.`}
        </p>
      ) : (
        <>
          {/*
            Pledge leads (owner-directed): a raised hand is what the stage is asking for, and no
            money moves on it. Give is the additional way, and it is the one drawn in the
            product's green — the same `--green` the received segment is painted with, in both
            schemes — never a new colour.
          */}
          <button
            type="button"
            className="pay appealcard__pledge"
            disabled={busy}
            onClick={() => onRespond("pledge")}
          >
            {busy ? "Working…" : `Pledge ${amount}`}
          </button>
          <p className="appealcard__pledge-note">Raises your hand — nothing is charged now.</p>
          <button
            type="button"
            className="pay pay--secondary appealcard__give"
            disabled={busy}
            onClick={() => onRespond("give")}
          >
            {busy ? "Opening…" : `Give ${amount}`}
          </button>
        </>
      )}

      {/*
        Committed, named as committed, and nothing else (appealPublicSchema's contract): every
        hand, pledge and gift at its rung's amount, summed by the server — never money received,
        and never folded into the thermometer above this card.
      */}
      <p className="appealcard__committed">
        <span className="appealcard__committed-value">{formatMoney(appeal.committed)}</span>{" "}
        committed · counted apart from money received
      </p>
    </section>
  );
}
